import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Input, Modal, Segmented, Space, Switch, Typography } from "antd";
import { DownloadOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { saveFile } from "../../../native/files";
import { emailReport, reportPdf } from "./api";
import "./siteDocs.css";

/**
 * Photo report (PDF) of the chosen entries: download it, or email it to the customer (with a
 * copy kept in the files). `selection` is { ids } or { filters }; `count` says how many entries.
 */
const ReportDialog = ({
  open,
  onClose,
  selection,
  count,
  defaultTitle,
  defaultEmail,
  canEmail
}) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(null); // download | email
  const [mode, setMode] = useState("download");

  useEffect(() => {
    if (!open) return;
    setMode("download");
    form.setFieldsValue({
      title: defaultTitle || "",
      remark: "",
      lng: i18n.language === "de" ? "de" : i18n.language === "en" ? "en" : "de",
      photoSize: "large",
      showAuthors: true,
      to: defaultEmail || "",
      message: ""
    });
  }, [open]);

  const bodyFrom = values => ({
    ...selection,
    title: values.title,
    remark: values.remark,
    lng: values.lng,
    photoSize: values.photoSize,
    showAuthors: values.showAuthors
  });

  const download = async () => {
    const values = form.getFieldsValue();
    setBusy("download");
    try {
      const { blob, fileName } = await reportPdf(bodyFrom(values));
      await saveFile(blob, fileName);
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy("email");
    try {
      const res = await emailReport({
        ...bodyFrom(values),
        to: values.to,
        message: values.message
      });
      Success(t("Report sent to {{to}}. A copy is kept in Documents.", { to: res.data.sentTo }));
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("Photo report (PDF)")}
      rootClassName="site-docs-page"
      destroyOnClose
      footer={
        <Space wrap className="sd-report-footer">
          <Button onClick={onClose}>{t("Cancel")}</Button>
          {mode === "email" ? (
            <Button
              type="primary"
              icon={<MailOutlined />}
              loading={busy === "email"}
              onClick={send}
            >
              {t("Send")}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={busy === "download"}
              onClick={download}
            >
              {t("Download PDF")}
            </Button>
          )}
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        {count === 1
          ? t("One entry with its notes and photos.")
          : t("{{n}} entries with their notes and photos, oldest first.", { n: count })}
      </Typography.Paragraph>
      {count > 100 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t("A report can hold at most 100 entries. Please narrow the selection.")}
        />
      )}
      {canEmail && (
        <Segmented
          block
          className="mb-3"
          value={mode}
          onChange={setMode}
          options={[
            { value: "download", label: t("Download"), icon: <DownloadOutlined /> },
            { value: "email", label: t("Email to customer"), icon: <MailOutlined /> }
          ]}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item name="title" label={t("Title (optional)")}>
          <Input maxLength={200} placeholder={t("e.g. Bathroom renovation, interim status")} />
        </Form.Item>
        <Form.Item name="remark" label={t("Remark for the customer (optional)")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={3000} />
        </Form.Item>
        <div className="sd-row">
          <Form.Item name="lng" label={t("Language")}>
            <Segmented
              options={[
                { value: "de", label: "Deutsch" },
                { value: "en", label: "English" }
              ]}
            />
          </Form.Item>
          <Form.Item name="photoSize" label={t("Photos")}>
            <Segmented
              options={[
                { value: "large", label: t("Large (2 per row)") },
                { value: "small", label: t("Small (3 per row)") }
              ]}
            />
          </Form.Item>
        </div>
        <Form.Item name="showAuthors" label={t("Show who documented")} valuePropName="checked">
          <Switch />
        </Form.Item>
        {mode === "email" && (
          <>
            <Form.Item
              name="to"
              label={t("Email address")}
              rules={[
                { required: true, type: "email", message: t("Please enter an email address.") }
              ]}
            >
              <Input type="email" autoComplete="email" />
            </Form.Item>
            <Form.Item
              name="message"
              label={t("Message (optional)")}
              extra={t("Without a message a short greeting is sent with the PDF.")}
            >
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={5000} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ReportDialog;
